import * as React from "react"
import { Slot } from "@radix-ui/react-slot"
import { cva, type VariantProps } from "class-variance-authority"

import { cn } from "@/lib/utils"

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium transition-[color,background-color,border-color,box-shadow] disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg:not([class*='size-'])]:size-4 [&_svg]:shrink-0 outline-none focus-visible:ring-[3px] focus-visible:ring-[#2d6a4f]/20 aria-invalid:border-red-500 aria-invalid:ring-red-500/20",
  {
    variants: {
      variant: {
        default:
          "bg-[#2d6a4f] text-white shadow-[0_1px_2px_rgba(28,38,32,0.1)] hover:bg-[#245741]",
        destructive:
          "bg-red-600 text-white shadow-[0_1px_2px_rgba(28,38,32,0.1)] hover:bg-red-700 focus-visible:ring-red-600/20",
        outline:
          "border border-[#dadbd5] bg-white text-[#1c2620] shadow-[0_1px_2px_rgba(28,38,32,0.04)] hover:bg-[#f4f5f1] hover:border-[#c5c8bf]",
        secondary:
          "bg-[#f0f2ee] text-[#1c2620] hover:bg-[#e6e9e2]",
        ghost: "text-[#3a4339] hover:bg-[#f0f2ee] hover:text-[#1c2620]",
        link: "text-[#2d6a4f] underline-offset-4 hover:underline",
      },
      size: {
        default: "h-9 px-4 py-2 has-[>svg]:px-3",
        sm: "h-8 rounded-md gap-1.5 px-3 has-[>svg]:px-2.5",
        lg: "h-10 rounded-md px-6 has-[>svg]:px-4",
        icon: "size-9",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
)

const Button = React.forwardRef<
  HTMLButtonElement,
  React.ComponentProps<"button"> &
    VariantProps<typeof buttonVariants> & {
      asChild?: boolean
    }
>(({ className, variant, size, asChild = false, ...props }, ref) => {
  const Comp = asChild ? Slot : "button"

  return (
    <Comp
      ref={ref}
      data-slot="button"
      className={cn(buttonVariants({ variant, size, className }))}
      {...props}
    />
  )
})
Button.displayName = "Button"

export { Button, buttonVariants }
